import { useState, useEffect, useRef, useCallback } from "react";
import debounce from "lodash/debounce";
import { ApiResponse } from "../api";

type SearchFn<T> = (params: { chatId?: string; query: string; page: number }) => Promise<ApiResponse<T[]>>;

export const useInfiniteSearch = <T,>({
  searchFn,
  chatId,
  debounceDelay = 400,
  initialQuery = "",
}: {
  searchFn: SearchFn<T>;
  chatId?: string;
  debounceDelay?: number;
  initialQuery?: string;
}) => {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useRef(
    debounce(async (q: string, chatId?: string) => {
      if (!q.trim()) {
        setResults([]);
        setTotalCount(0);
        setHasMore(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const res = await searchFn({ chatId, query: q, page: 1 });

        if (res.success) {
          setResults(res.result);
          setTotalCount(res.count);
          setHasMore(res.result.length < res.count);
        } else {
          // В случае ошибки — очищаем результаты
          setResults([]);
          setTotalCount(0);
          setHasMore(false);
          console.warn("Search API error:", res.error);
        }
      } catch (err) {
        console.error("Unexpected search error:", err);
        setResults([]);
        setTotalCount(0);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    }, debounceDelay)
  ).current;

  useEffect(() => {
    debouncedSearch(query, chatId);
  }, [query]);

  const loadMore = useCallback(async () => {
    if (
      !containerRef.current ||
      !chatId  ||
      loading ||
      !hasMore ||
      totalCount <= results.length
    )
      return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      setLoading(true);
      try {
        const nextPage = page + 1;
        const res = await searchFn({ chatId, query, page: nextPage });

        if (res.success) {
          setResults((prev) => [...prev, ...res.result]);
          setPage(nextPage);
          setHasMore(results.length + res.result.length < res.count);
        } else {
          setHasMore(false);
          console.warn("Load more API error:", res.error);
        }
      } catch (err) {
        console.error("Load more unexpected error:", err);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    }
  }, [query, page, chatId, hasMore, results.length, totalCount, loading]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener("scroll", loadMore);
    return () => container.removeEventListener("scroll", loadMore);
  }, [loadMore]);

  const reset = useCallback(() => {
    // setQuery("");
    setResults([]);
    setPage(1);
    setHasMore(true);
    setTotalCount(0);
  }, []);

  return {
    query,
    setQuery,
    results,
    loading,
    hasMore,
    totalCount,
    containerRef,
    reset,
    handleSearch: debouncedSearch,
  };
};